import { useState, useEffect, useMemo } from 'react';
import Modal from '../../../components/Modal';
import AssetPreviewLightbox, { PreviewAsset } from './AssetPreviewLightbox';
import ShotVideoRefPanel, { listVideoShotEntries } from './ShotVideoRefPanel';
import { IneligibleReason } from '../constants';
import { AssetRef, ProjectAsset, Resource, ResourceReviewItem, Shot, ShotVersion } from '../../../types';
import { tosThumbnail } from '../../../utils/tosImage';
import { tosAccelerate } from '../../../utils/tosAccelerate';
import { countShotRefs } from '../../../utils/videoLimits';
import { AnalysisAssetItem, AnalysisAssetsState, AssetPickerTab, AssetPickerTabKey } from '../types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface AssetPickerModalProps {
  open: boolean;
  addingAssets: boolean;
  pickingAssetFor: string | null;
  pickerTab: AssetPickerTabKey;
  pickerTabs: AssetPickerTab[];
  pickerSearch: string;
  pickerSelected: AnalysisAssetItem[];
  analysisAssets: AnalysisAssetsState;
  projectActiveReviews: ResourceReviewItem[];
  shots: Shot[];
  shotResources: Record<string, Resource[]>;
  projectId: string;
  // 单分镜引用+资源的总数上限，来自项目所选视频模型。undefined 或 0 = 不限。
  maxRefTotal?: number;
  t: Translate;
  getAssetRefs: (shot: Shot) => AssetRef[];
  onClose: () => void;
  onTabChange: (tab: AssetPickerTabKey) => void;
  onSearchChange: (search: string) => void;
  onSelectedChange: React.Dispatch<React.SetStateAction<AnalysisAssetItem[]>>;
  onToggleAsset: (asset: AnalysisAssetItem) => void;
  onAddAssets: (shotId: string, assets: AnalysisAssetItem[]) => void;
  projectAssets: ProjectAsset[];
  librarySelectedIds: string[];
  onLibrarySelectedChange: React.Dispatch<React.SetStateAction<string[]>>;
  onAddLibraryAssets: (shotId: string, assets: ProjectAsset[]) => void;
  shotVersions: Record<string, ShotVersion[]>;
  selectedVideoVersions: Record<string, string>;
  videoPlatform?: string;
  addingVideoRef: boolean;
  ineligibleLabel: (reason: IneligibleReason) => string;
  ineligibleTitle: (reason: IneligibleReason) => string;
  onAddVideoRef: (shotId: string, sourceShotIndex: number, videoURL: string) => void;
  onRefreshAssets: () => Promise<void>;
}

export default function AssetPickerModal({
  open,
  addingAssets,
  pickingAssetFor,
  pickerTab,
  pickerTabs,
  pickerSearch,
  pickerSelected,
  analysisAssets,
  projectActiveReviews,
  shots,
  shotResources,
  projectId,
  maxRefTotal,
  t,
  getAssetRefs,
  onClose,
  onTabChange,
  onSearchChange,
  onSelectedChange,
  onAddAssets,
  projectAssets,
  librarySelectedIds,
  onLibrarySelectedChange,
  onAddLibraryAssets,
  shotVersions,
  selectedVideoVersions,
  videoPlatform,
  addingVideoRef,
  ineligibleLabel,
  ineligibleTitle,
  onAddVideoRef,
  onRefreshAssets,
}: AssetPickerModalProps) {
  // 二级弹窗：选中某个资产后，展示其所有状态图供用户挑选最终使用的那张
  const [stateForAsset, setStateForAsset] = useState<AnalysisAssetItem | null>(null);
  const [chosenState, setChosenState] = useState<{ state: string; imageUrl: string } | null>(null);
  // 已审核/其他素材 tab 的资源预览（图片/视频/音频）
  const [previewAsset, setPreviewAsset] = useState<PreviewAsset | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // 一级弹窗关闭（含添加成功后自动关闭）时清理二级弹窗的本地状态，保证两层一起消失
  useEffect(() => {
    if (!open) {
      setStateForAsset(null);
      setChosenState(null);
      setPreviewAsset(null);
    }
  }, [open]);

  // 行内预览按钮（阻止冒泡，避免触发行选中）
  const previewButton = (asset: PreviewAsset) => (
    <button
      onClick={e => { e.stopPropagation(); setPreviewAsset(asset); }}
      style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', padding: '2px 10px', fontSize: '0.75rem', color: 'var(--text-2)', flexShrink: 0 }}
    >
      {t('pickerModal.preview')}
    </button>
  );

  const isReviewedTab = pickerTab === 'reviewed';
  const isLibraryTab = pickerTab === 'library';
  const isShotVideosTab = pickerTab === 'shotVideos';
  // pickerTab 类型含 'reviewed'/'library'/'shotVideos'，不能直接索引 AnalysisAssetsState——显式收窄
  const analysisTabKey = (isReviewedTab || isLibraryTab || isShotVideosTab) ? null : (pickerTab as 'characters' | 'scenes' | 'props');
  // tab 计数与面板共用同一筛选：排除当前分镜、completed+videoURL；Ark 不可用项仍计入
  const videoShotEntries = listVideoShotEntries({
    shots, shotVersions, selectedVideoVersions, currentShotId: pickingAssetFor, videoPlatform,
  });
  const currentPickerAssets = analysisTabKey === null ? [] : [...analysisAssets[analysisTabKey]]
    .filter(asset => !pickerSearch || asset.name.toLowerCase().includes(pickerSearch.toLowerCase()))
    .sort((a, b) => Number(b.hasImage) - Number(a.hasImage));
  const currentPickerElemType = pickerTabs.find(tab => tab.key === pickerTab)?.elemType || 'character';
  const projectAnalysisUrl = (elemType: string, name: string) =>
    `/projects/${projectId}?openElement=${elemType}:${encodeURIComponent(name)}`;
  const ADD_ELEMENT_BY_TAB = { characters: 'character', scenes: 'scene', props: 'prop' } as const;
  const uniqueProjectActiveReviews = useMemo(() => {
    const seen = new Set<string>();
    return projectActiveReviews.filter(review => {
      const key = review.resourceUrl || review.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [projectActiveReviews]);

  // 资源数量上限：单分镜的引用 + 资源总数，按项目所选视频模型的 maxRefTotal 计。
  // maxRefTotal 未传（限制未解析）或为 0（模型不限）时**不设上限**：remaining 记为 null，
  // 既不显示"还可添加 N 个"的具体数字，也绝不禁用添加按钮。
  // 千万不要在这种情况下让 remaining 落成 0 或负数 —— 那会把整个选择器永久锁死。
  // existingCount 必须与守卫/后端同一份计数口径（countShotRefs）：排除 preset_audio 的
  // Resource、排除 imageUrl 未解析的 AssetRef。用裸 .length 会多算，接到模型真实上限
  // （可能只有 4-5）后就会把「添加」按钮误置灰 —— 而守卫和后端其实都放行，方向是误伤。
  const shot = pickingAssetFor ? shots.find(item => item.id === pickingAssetFor) : null;
  const existingCount = shot
    ? countShotRefs(getAssetRefs(shot), shotResources[pickingAssetFor!] || []).total
    : 0;
  const hasTotalLimit = typeof maxRefTotal === 'number' && maxRefTotal > 0;
  const remaining: number | null = hasTotalLimit ? maxRefTotal! - existingCount : null;
  const noRoomLeft = remaining !== null && remaining <= 0;
  const resourceCountLabel = remaining === null
    ? t('pickerModal.resourceCountUnlimited', { existing: existingCount })
    : t('pickerModal.resourceCount', { existing: existingCount, remaining });

  // 二级弹窗网格从实时 analysisAssets 取该资产状态（刷新后自动反映新状态）；失配回退快照
  const PLURAL_KEY = { character: 'characters', scene: 'scenes', prop: 'props' } as const;
  const liveStateAsset = stateForAsset && stateForAsset.type !== 'reviewed'
    ? (analysisAssets[PLURAL_KEY[stateForAsset.type]].find(a => a.name === stateForAsset.name) ?? stateForAsset)
    : stateForAsset;

  // 刷新后若原选中状态已不存在，清空 chosenState（避免用已消失的状态落库）
  useEffect(() => {
    if (!chosenState || !liveStateAsset) return;
    const stillExists = liveStateAsset.allStates.some(
      s => s.state === chosenState.state && s.imageUrl === chosenState.imageUrl,
    );
    if (!stillExists) setChosenState(null);
  }, [liveStateAsset, chosenState]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefreshAssets();
    } finally {
      setRefreshing(false);
    }
  };

  const closeStateModal = () => { setStateForAsset(null); setChosenState(null); };
  const handleConfirmState = () => {
    if (!chosenState || !liveStateAsset || !pickingAssetFor) return;
    onAddAssets(pickingAssetFor, [{ ...liveStateAsset, state: chosenState.state, imageUrl: chosenState.imageUrl, hasImage: true }]);
  };

  // 二级状态图弹窗「＋ 新增状态」深链（reviewed 无状态概念，不渲染该卡）
  const addStateHref = stateForAsset && stateForAsset.type !== 'reviewed'
    ? `/projects/${projectId}?openElement=${stateForAsset.type}:${encodeURIComponent(stateForAsset.name)}&addState=1`
    : null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={t('pickerModal.title')}
        width="1280px"
        contentStyle={{ background: 'var(--bg-2, #f5f5f4)' }}
        footer={(() => {
          const directCount = isLibraryTab ? librarySelectedIds.length : pickerSelected.length;
          const overLimit = remaining !== null && directCount > remaining;
          const showDirectAdd = isReviewedTab || isLibraryTab;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ fontSize: '0.8rem', color: (showDirectAdd && overLimit) ? 'var(--danger)' : 'var(--text-3)' }}>
                {resourceCountLabel}{showDirectAdd && directCount > 0 ? t('pickerModal.selected', { count: directCount }) : ''}
              </span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-secondary" onClick={onClose} disabled={addingAssets || addingVideoRef}>{t('cancel', { ns: 'common' })}</button>
                {/* 「已审核素材」与「其他素材」tab 直接多选添加；「引用已生成视频」点卡片立即添加；其余 tab 点资产→选状态→添加 */}
                {showDirectAdd && (
                  <button
                    className="btn"
                    style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: '#fff', border: 'none' }}
                    disabled={directCount === 0 || overLimit || addingAssets}
                    onClick={() => {
                      if (!pickingAssetFor) return;
                      if (isLibraryTab) {
                        onAddLibraryAssets(pickingAssetFor, projectAssets.filter(a => librarySelectedIds.includes(a.id)));
                      } else {
                        onAddAssets(pickingAssetFor, pickerSelected);
                      }
                    }}
                  >
                    {addingAssets ? t('pickerModal.adding') : t('pickerModal.addCount', { count: directCount })}
                  </button>
                )}
              </div>
            </div>
          );
        })()}
      >
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', gap: '0', marginBottom: '1rem' }}>
          {pickerTabs.map(tab => {
            const count = tab.key === 'reviewed'
              ? uniqueProjectActiveReviews.length
              : tab.key === 'library'
                ? projectAssets.length
                : tab.key === 'shotVideos'
                  ? videoShotEntries.length
                  : analysisAssets[tab.key]?.length || 0;
            return (
              <button
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                style={{ padding: '6px 16px', background: 'none', border: 'none', borderBottom: pickerTab === tab.key ? '2px solid var(--theme-storyboard)' : '2px solid transparent', color: pickerTab === tab.key ? 'var(--theme-storyboard)' : 'var(--text-2)', fontWeight: pickerTab === tab.key ? 600 : 400, cursor: 'pointer', fontSize: '0.9rem', marginBottom: '-1px' }}
              >
                {tab.label}
                {count > 0 && (
                  <span style={{ marginLeft: '4px', fontSize: '0.75rem', color: 'var(--text-3)' }}>({count})</span>
                )}
              </button>
            );
          })}
        </div>

        {isShotVideosTab ? (
          <ShotVideoRefPanel
            currentShotId={pickingAssetFor}
            addingVideoRef={addingVideoRef}
            shots={shots}
            shotVersions={shotVersions}
            selectedVideoVersions={selectedVideoVersions}
            videoPlatform={videoPlatform}
            t={t}
            getAssetRefs={getAssetRefs}
            ineligibleLabel={ineligibleLabel}
            ineligibleTitle={ineligibleTitle}
            onAddVideoRef={onAddVideoRef}
          />
        ) : isLibraryTab ? (
          <div style={{ overflowY: 'auto', maxHeight: 'none' }}>
            {projectAssets.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>{t('pickerModal.noLibraryAssets')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {projectAssets.map(asset => {
                  const isSelected = librarySelectedIds.includes(asset.id);
                  return (
                    <div
                      key={asset.id}
                      onClick={() => {
                        if (addingAssets) return;
                        onLibrarySelectedChange(prev =>
                          prev.includes(asset.id) ? prev.filter(id => id !== asset.id) : [...prev, asset.id]);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '8px 12px', borderRadius: 6,
                        border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                        background: isSelected ? 'rgba(24,144,255,0.05)' : 'var(--surface)',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: 4, overflow: 'hidden', flexShrink: 0, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {asset.type === 'image' ? (
                          <img src={tosThumbnail(asset.url, 96)} alt={asset.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: '1.5rem' }}>🎬</span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                          {asset.type === 'image' ? t('pickerModal.image') : t('pickerModal.video')}
                          {asset.type === 'video' && asset.duration > 0 ? ` · ${t('pickerModal.assetDuration', { duration: asset.duration.toFixed(1) })}` : ''}
                          {asset.originShotIndex > 0 ? ` · ${t('pickerModal.sourceShot', { index: asset.originShotIndex })}` : ''}
                        </div>
                      </div>
                      {previewButton({ type: asset.type, url: asset.url, name: asset.name })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : isReviewedTab ? (
          <div style={{ overflowY: 'auto', maxHeight: 'none' }}>
            {uniqueProjectActiveReviews.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>{t('pickerModal.noReviewedAssets')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {uniqueProjectActiveReviews.map(review => {
                  const isSelected = pickerSelected.some(asset => asset.type === 'reviewed' && asset.reviewId === review.id);
                  return (
                    <div
                      key={review.id}
                      onClick={() => {
                        if (addingAssets) return;
                        const selected = { type: 'reviewed' as const, reviewId: review.id, name: review.name, state: '__reviewed__', imageUrl: review.resourceUrl, hasImage: true, allStates: [] };
                        onSelectedChange(prev =>
                          prev.some(asset => asset.type === 'reviewed' && asset.reviewId === review.id)
                            ? prev.filter(asset => !(asset.type === 'reviewed' && asset.reviewId === review.id))
                            : [...prev, selected]
                        );
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '8px 12px', borderRadius: 6,
                        border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                        background: isSelected ? 'rgba(24,144,255,0.05)' : 'var(--surface)',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: 4, overflow: 'hidden', flexShrink: 0, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {review.resourceType === 'image' ? (
                          <img src={tosAccelerate(review.resourceUrl)} alt={review.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : review.resourceType === 'video' ? (
                          <span style={{ fontSize: '1.5rem' }}>🎬</span>
                        ) : (
                          <span style={{ fontSize: '1.5rem' }}>🎵</span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{review.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                          {review.resourceType === 'image' ? t('pickerModal.image') : review.resourceType === 'video' ? t('pickerModal.video') : t('pickerModal.audio')}
                        </div>
                      </div>
                      {previewButton({ type: review.resourceType, url: review.resourceUrl, name: review.name })}
                      <span style={{ fontSize: '0.75rem', color: 'var(--success)' }}>{t('pickerModal.reviewedBadge')}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            {analysisTabKey !== null && analysisAssets[analysisTabKey].length > 6 && (
              <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--theme-storyboard)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder=""
                  value={pickerSearch}
                  onChange={event => onSearchChange(event.target.value)}
                  style={{ width: '100%', padding: '8px 12px 8px 34px', border: 'none', borderRadius: 'var(--radius)', fontSize: '0.85rem', outline: 'none', background: 'var(--surface, #fff)', boxSizing: 'border-box', boxShadow: 'var(--shadow-xs, 0 1px 6px rgba(0,0,0,0.06))', color: 'var(--text)' }}
                />
              </div>
            )}
            <div style={{ overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', minHeight: '80px', maxHeight: 'none', paddingRight: '10px' }}>
              {currentPickerAssets.length === 0 && (
                <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', margin: 0, gridColumn: '1 / -1' }}>{t('pickerModal.noResources')}</p>
              )}
              {currentPickerAssets.map(asset => {
                if (asset.hasImage) {
                  // 点击资产卡片 → 打开二级状态弹窗（不再在一级直接勾选/添加）
                  return (
                    <div
                      key={`${asset.type}-${asset.name}-${asset.state}`}
                      onClick={() => !addingAssets && setStateForAsset(asset)}
                      style={{ width: '100%', border: 'none', borderRadius: 'var(--radius)', overflow: 'hidden', cursor: 'pointer', background: 'var(--surface, #fff)', boxShadow: 'var(--shadow-xs, 0 1px 6px rgba(0,0,0,0.05))', transition: 'box-shadow var(--transition-fast)', position: 'relative' }}
                    >
                      <img src={tosThumbnail(asset.imageUrl, 200)} alt={asset.name} style={{ width: '100%', aspectRatio: '1', objectFit: 'contain', display: 'block' }} />
                      <div style={{ padding: '4px 6px' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</div>
                        {asset.state && <div style={{ fontSize: '0.7rem', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.state}</div>}
                        {asset.allStates.length > 1 && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)' }}>{t('pickerModal.stateCount', { count: asset.allStates.length })}</div>}
                      </div>
                    </div>
                  );
                }
                return (
                  <a
                    key={`${asset.type}-${asset.name}-incomplete`}
                    href={projectAnalysisUrl(currentPickerElemType, asset.name)}
                    target="_blank"
                    rel="noreferrer"
                    title={t('pickerModal.goSelectTitle')}
                    style={{ width: '100%', border: 'none', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--surface, #fff)', boxShadow: 'var(--shadow-xs, 0 1px 6px rgba(0,0,0,0.05))', textDecoration: 'none', display: 'block', cursor: 'pointer' }}
                  >
                    <div style={{ width: '100%', aspectRatio: '1', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '1.2rem', opacity: 0.4 }}>🖼️</span>
                      <span style={{ fontSize: '0.62rem', color: 'var(--text-3)' }}>{t('pickerModal.notSelected')}</span>
                      <span style={{ fontSize: '0.6rem', color: 'var(--primary)' }}>{t('pickerModal.goSelect')}</span>
                    </div>
                    <div style={{ padding: '4px 6px' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</div>
                    </div>
                  </a>
                );
              })}
              {analysisTabKey !== null && (
                <a
                  href={`/projects/${projectId}?addElement=${ADD_ELEMENT_BY_TAB[analysisTabKey]}`}
                  target="_blank"
                  rel="noreferrer"
                  title={t('pickerModal.addAsset')}
                  style={{ width: '100%', border: '1px dashed var(--theme-storyboard)', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--surface, #fff)', textDecoration: 'none', display: 'block', cursor: 'pointer' }}
                >
                  <div style={{ width: '100%', aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', color: 'var(--theme-storyboard)' }}>
                    <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: '0.75rem' }}>{t('pickerModal.addAsset')}</span>
                  </div>
                </a>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* 二级弹窗：以与一级相同的网格排版展示该资产所有状态图，选一张后点添加直接落库 */}
      <Modal
        open={!!stateForAsset}
        onClose={closeStateModal}
        title={t('pickerModal.pickStateTitle', { name: stateForAsset?.name || '' })}
        titleExtra={stateForAsset ? (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label={t('pickerModal.refreshStates')}
            title={t('pickerModal.refreshStates')}
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: refreshing ? 'default' : 'pointer', padding: '4px 8px', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', opacity: refreshing ? 0.6 : 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={refreshing ? { animation: 'spin 0.8s linear infinite' } : undefined}>
              <path d="M23 4v6h-6M1 20v-6h6" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        ) : undefined}
        width="1280px"
        contentStyle={{ background: 'var(--bg-2, #f5f5f4)' }}
        footer={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ fontSize: '0.8rem', color: noRoomLeft ? 'var(--danger)' : 'var(--text-3)' }}>
              {resourceCountLabel}
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={closeStateModal} disabled={addingAssets}>{t('cancel', { ns: 'common' })}</button>
              <button className="btn" style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: '#fff', border: 'none' }} disabled={!chosenState || noRoomLeft || addingAssets} onClick={handleConfirmState}>
                {addingAssets ? t('pickerModal.adding') : t('pickerModal.add')}
              </button>
            </div>
          </div>
        }
      >
        {stateForAsset && liveStateAsset && (
          <div style={{ overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', minHeight: '80px', maxHeight: 'none', paddingRight: '10px' }}>
            {liveStateAsset.allStates.length === 0 && (
              <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', margin: 0, gridColumn: '1 / -1' }}>{t('pickerModal.pickStateEmpty')}</p>
            )}
            {liveStateAsset.allStates.map(st => {
              const generated = !!st.imageUrl;
              const isSelected = !!chosenState && chosenState.state === st.state && chosenState.imageUrl === st.imageUrl;
              return (
                <div
                  key={`${st.state}::${st.imageUrl}`}
                  onClick={() => generated && !addingAssets && setChosenState({ state: st.state, imageUrl: st.imageUrl })}
                  style={{ width: '100%', border: isSelected ? '2px solid var(--theme-storyboard)' : 'none', borderRadius: 'var(--radius)', overflow: 'hidden', cursor: generated ? 'pointer' : 'default', background: 'var(--surface, #fff)', boxShadow: isSelected ? 'none' : 'var(--shadow-xs, 0 1px 6px rgba(0,0,0,0.05))', transition: 'box-shadow var(--transition-fast)', position: 'relative', opacity: generated ? 1 : 0.6 }}
                >
                  {isSelected && (
                    <div style={{ position: 'absolute', top: '4px', right: '4px', width: '20px', height: '20px', borderRadius: '50%', background: 'var(--theme-storyboard)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', zIndex: 1 }}>✓</div>
                  )}
                  {generated ? (
                    <img src={tosThumbnail(st.imageUrl, 200)} alt={st.state} style={{ width: '100%', aspectRatio: '1', objectFit: 'contain', display: 'block' }} />
                  ) : (
                    <div style={{ width: '100%', aspectRatio: '1', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', color: 'var(--warning)' }}>
                      <span style={{ fontSize: '1.2rem' }}>⚠</span>
                      <span style={{ fontSize: '0.62rem' }}>{t('pickerModal.stateNotGenerated')}</span>
                    </div>
                  )}
                  <div style={{ padding: '4px 6px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.state || t('pickerModal.defaultState')}</div>
                  </div>
                </div>
              );
            })}
            {addStateHref && (
              <button
                type="button"
                onClick={() => window.open(addStateHref, '_blank', 'noopener')}
                title={t('assetState.addState', { ns: 'common' })}
                style={{ width: '100%', aspectRatio: '1', border: '1px dashed var(--theme-storyboard)', borderRadius: 'var(--radius)', background: 'var(--surface, #fff)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', color: 'var(--theme-storyboard)' }}
              >
                <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>+</span>
                <span style={{ fontSize: '0.8rem' }}>{t('assetState.addState', { ns: 'common' })}</span>
              </button>
            )}
          </div>
        )}
      </Modal>

      {/* 已审核/其他素材的资源预览 */}
      <AssetPreviewLightbox asset={previewAsset} onClose={() => setPreviewAsset(null)} />
    </>
  );
}
